import { t } from 'i18next';
import { Color, Vector3 } from 'three';

import { ContextMenuItem } from '../../../../../../../react/components/skyview/context-menu';
import { isDefined } from '../../../../../../../utils/variables';
import { CoordinateManager } from '../../../../../../coordinate-manager';
import { RevertChange } from '../../../../../../elements/design/design-node/design-node';
import { getSegmentCurve } from '../../../../../../elements/design/design-node/utils/conic';
import {
  findCrossedLineSegmentIdFromIntersection,
  findLineSegmentIdFromIntersection,
  LineSegmentId,
  recalculateLineSegmentMetaData,
} from '../../../../../../elements/design/design-node/utils/line-segment-node';
import {
  findPointIdFromIntersection,
  PointId,
} from '../../../../../../elements/design/design-node/utils/point-node';
import { MouseInputData } from '../../../../../../utilities/input-event/input-event-data-extractor';
import { ActionInProgress, ActionName, DesignTool } from '../../../../../design-tool';
import { PickingLine } from '../../../../../picking-line';
import { PickingSphere } from '../../../../../picking-sphere';
import { AssetLine } from '../../../../asset-line';
import { addAndCommitAction } from '../../utils/actions';
import {
  addArcLineSegments,
  canHaveArcMeasure,
  CurveShape,
  getAlignedShape,
  getArcBulge,
  getArcCurves,
  getArcMeasure,
  getConicCursorVertices,
  getCurveArcs,
  getCurveThroughPoint,
  getLocalShape,
  reverseCurveShape,
} from '../../utils/curve';
import { createNewPoint, setToolEditIntersectionToPoint } from '../../utils/point';
import { abortActionInProgress, applyActionInProgress, initActionInProgress } from '../general';
import { createLine } from '../line/create';

type CurveEnd = {
  position: Vector3;
  pointId?: PointId;
  lineSegmentId?: LineSegmentId;
  /** The line segment that lineSegmentId crosses at the end, see CrossingIntersection. */
  crossedLineSegmentId?: LineSegmentId;
  /** The line of another asset that the end is on. */
  assetLine?: AssetLine;
};

type CurveOptions = {
  /** Where the curve starts, at the cursor by default. */
  start?: CurveEnd;
  /** Places the end at the cursor right away, so that the next click bends the curve. */
  endAtCursor?: boolean;
  /** What is created on from the end of the curve, another curve by default. */
  next?: 'curve' | 'line';
};

const definedAndEqual = <T extends LineSegmentId | PointId | undefined>(a: T, b: T) =>
  isDefined(a) && a === b;

/**
 * Creates a curved line segment in three clicks: the start, the end, and a point the curve passes through.
 * The curve continues from the end, like when creating lines.
 *
 * Holding Alt, the line segment is straight instead, created with the click on its end. Except
 * when the curve is created on from a line, see bendLineInProgress, which Alt already bent.
 * Holding Shift while bending it, the curve continues the line that ends at its start, and goes on
 * into the line that ends at its end, see CurveShape.
 */
export const createCurve = (tool: DesignTool, options: CurveOptions = {}): ContextMenuItem => {
  const next = options.next ?? 'curve';
  const startPoint = tool.currentPoint?.clone();
  const disabled = !isDefined(options.start) && !isDefined(startPoint);

  const start: CurveEnd | undefined =
    options.start ??
    (startPoint && {
      position: startPoint,
      pointId: findPointIdFromIntersection(tool.asset.designNode, tool.intersection),
      lineSegmentId: findLineSegmentIdFromIntersection(tool.asset.designNode, tool.intersection),
      crossedLineSegmentId: findCrossedLineSegmentIdFromIntersection(
        tool.asset.designNode,
        tool.intersection,
      ),
      assetLine: tool.intersection ? undefined : tool.assetLine,
    });

  return {
    title: t('contextMenu.create.items.curve', { ns: 'skyviewDesign' }),
    action: () => {
      if (disabled || !start) {
        return;
      }

      let end: CurveEnd | undefined;

      const startCursor = new PickingSphere(new Color(0.75, 0.75, 0.75));
      startCursor.position.copy(start.position);

      const endCursor = new PickingSphere(new Color(0.75, 0.75, 0.75));
      endCursor.visible = false;

      const curveCursor = new PickingLine();

      const getThroughPoint = (curveEnd: CurveEnd) =>
        getCurveThroughPoint(tool, (start.position.z + curveEnd.position.z) / 2);

      const measure = tool.curveMeasure;
      // The radius or bulge (meters) of the curve, see CurveMeasure, when it is typed.
      let typedDistance: number | undefined;

      const straight = () => next === 'curve' && tool.inputHandler.altIsDown;

      /**
       * The shape of the curve: a circular arc through the cursor, or with the typed distance. While
       * Shift is held, it follows the lines at its ends instead, at each end the line closest to
       * the direction of that arc there.
       */
      const getShape = (curveEnd: CurveEnd): CurveShape => {
        if (straight()) {
          return { bulge: 0 };
        }

        const bulge = getArcBulge(
          start.position,
          curveEnd.position,
          getThroughPoint(curveEnd),
          isDefined(typedDistance) ? { measure, distance: typedDistance } : undefined,
        );

        return (
          (tool.inputHandler.shiftIsDown &&
            getAlignedShape(tool.asset.designNode, start, curveEnd, bulge)) || { bulge }
        );
      };

      const getConics = (curveEnd: CurveEnd) =>
        getCurveArcs(start.position, curveEnd.position, getShape(curveEnd)).flatMap((arc) =>
          getArcCurves(arc.start, arc.end, arc.bulge),
        );

      /**
       * Creates the curve and continues with a new curve, or a line, from its end.
       */
      const finish = () => {
        if (!end) {
          return;
        }

        const endPointId = applyCurve(tool, start, end, getShape(end));
        void applyActionInProgress(tool).action();

        if (isDefined(endPointId)) {
          setToolEditIntersectionToPoint(tool, endPointId);
          void (next === 'line' ? createLine(tool) : createCurve(tool)).action();
          tool.onMouseMove(tool.lastMouseEvent);
        }
      };

      /**
       * Places the end of the curve at the cursor, unless it is at the start.
       */
      const placeEnd = () => {
        if (!isDefined(tool.currentPoint)) {
          return;
        }

        const pointId = findPointIdFromIntersection(tool.asset.designNode, tool.intersection);
        const lineSegmentId = findLineSegmentIdFromIntersection(
          tool.asset.designNode,
          tool.intersection,
        );

        if (
          definedAndEqual(start.pointId, pointId) ||
          definedAndEqual(start.lineSegmentId, lineSegmentId) ||
          start.position.distanceTo(tool.currentPoint) === 0
        ) {
          return;
        }

        const curveEnd: CurveEnd = {
          position: tool.currentPoint.clone(),
          pointId,
          lineSegmentId,
          crossedLineSegmentId: findCrossedLineSegmentIdFromIntersection(
            tool.asset.designNode,
            tool.intersection,
          ),
        };
        end = curveEnd;
        endCursor.position.copy(curveEnd.position);
        endCursor.visible = true;

        // The point the curve passes through is not constrained by the start.
        action.origin = undefined;
        action.originLineSegmentId = undefined;
        action.originPointId = undefined;
        action.originCrossedLineSegmentId = undefined;
        action.originAssetLine = undefined;

        // The radius or bulge of the curve can also be typed in the tooltip.
        tool.curveDistanceInput = {
          kind: measure,
          // Of the first arc, when the curve follows the lines at both its ends
          getDistance: () => {
            const [arc] = getCurveArcs(start.position, curveEnd.position, getShape(curveEnd));
            return arc ? getArcMeasure(arc.start, arc.end, arc.bulge, measure) : Infinity;
          },
          setDistance: (distance) => {
            typedDistance = distance;
            action.refresh?.();
            tool.updateTooltip();
          },
          isValid: (distance) =>
            canHaveArcMeasure(start.position, curveEnd.position, distance, measure),
          apply: finish,
          cancel: () => void abortActionInProgress(tool).action(),
        };
      };

      const action: ActionInProgress = {
        actionName: ActionName.CreateCurve,
        // The end is placed with the line constraints from the start, like when creating lines.
        origin: start.position,
        originLineSegmentId: start.lineSegmentId,
        originPointId: start.pointId,
        originCrossedLineSegmentId: start.crossedLineSegmentId,
        originAssetLine: start.assetLine,
        initiate: () => {
          tool.add(startCursor);
          tool.add(endCursor);
          tool.add(curveCursor);
          tool.onEdited?.();
        },
        cleanup: () => {
          tool.remove(startCursor);
          tool.remove(endCursor);
          tool.remove(curveCursor);
          tool.curveDistanceInput = undefined;
        },
        refresh: () => {
          if (!end) {
            curveCursor.setVertices([start.position, tool.currentPoint ?? start.position]);
            return;
          }

          const through = getThroughPoint(end);

          // Show the cursor where the curve passes through, unless the distance is typed.
          if (through && !isDefined(typedDistance)) {
            tool.cursorPoint.position.copy(through);
          }

          const conics = getConics(end);
          curveCursor.setVertices(
            conics.length > 0 ? getConicCursorVertices(conics) : [start.position, end.position],
          );
        },
        onProjectProjectionUpdated: (origoDiff) => {
          start.position.add(origoDiff);
          startCursor.position.add(origoDiff);

          if (end) {
            end.position.add(origoDiff);
            endCursor.position.add(origoDiff);
          }
        },
        readyToApply: () => isDefined(tool.currentPoint),
        onMouseUp: (e: MouseInputData) => {
          if (!end) {
            placeEnd();

            // Holding Alt, the line segment is straight, and done
            if (end && straight()) {
              finish();
            } else {
              tool.onMouseMove(e);
            }
            return;
          }

          finish();
        },
        apply: () => {
          // Applied in onMouseUp, since the curve needs several clicks.
        },
      };

      initActionInProgress(tool, action);

      if (options.endAtCursor) {
        placeEnd();
        tool.onMouseMove(tool.lastMouseEvent);
      }

      tool.contextMenuCallback?.();
    },
    disabled,
  };
};

/**
 * Makes the line being created a curve, ending at the cursor, which the next click bends, as when
 * Alt is held while placing the end of a line. Lines are then created on from the end of the curve.
 */
export const bendLineInProgress = (tool: DesignTool) => {
  const line = tool.actionInProgress;

  if (line?.actionName !== ActionName.CreateLine || !isDefined(line.origin)) {
    return;
  }

  void createCurve(tool, {
    start: {
      position: line.origin.clone(),
      pointId: line.originPointId,
      lineSegmentId: line.originLineSegmentId,
      crossedLineSegmentId: line.originCrossedLineSegmentId,
      assetLine: line.originAssetLine,
    },
    endAtCursor: true,
    next: 'line',
  }).action();
};

/**
 * Creates the curve, circular arcs with the shape, reusing points and splitting line segments at
 * the start and end. If there already is a line segment between the start and end it is curved
 * instead. An arc too large for one curve is several line segments, with points between them, as
 * are the arcs of a curve with several.
 * Returns the id of the end point.
 */
function applyCurve(
  tool: DesignTool,
  start: CurveEnd,
  end: CurveEnd,
  shape: CurveShape,
): PointId | undefined {
  const node = tool.asset.designNode;
  const coordinateManager = CoordinateManager.getInstance();
  const startCoordinate = coordinateManager.localToProjectCoords(start.position);
  const endCoordinate = coordinateManager.localToProjectCoords(end.position);
  const activeAttributes = tool.getActiveAttributes();

  let changes: RevertChange[] = [];
  let endPointId: PointId | undefined;

  addAndCommitAction(tool, {
    commit: () => {
      const startResult = createNewPoint(
        tool,
        startCoordinate,
        start.lineSegmentId,
        start.pointId,
        activeAttributes,
        start.crossedLineSegmentId,
      );
      const endResult = createNewPoint(
        tool,
        endCoordinate,
        end.lineSegmentId,
        end.pointId,
        activeAttributes,
        end.crossedLineSegmentId,
      );

      changes = [...endResult.revertChanges, ...startResult.revertChanges];
      endPointId = endResult.id;

      const existing = node.findLineSegmentByPointIds(startResult.id, endResult.id);
      // In the direction of the line segment, if there is one.
      const [from, to] = existing?.values ?? [startResult.id, endResult.id];
      const arcs = getCurveArcs(
        node.getVertexByPointId(from, 'local'),
        node.getVertexByPointId(to, 'local'),
        getLocalShape(node, from === startResult.id ? shape : reverseCurveShape(shape)),
      );
      const pieces = arcs.map((arc) => getArcCurves(arc.start, arc.end, arc.bulge));

      if (isDefined(existing) && pieces.length <= 1 && (pieces[0]?.length ?? 0) <= 1) {
        const conic = pieces[0]?.[0];
        changes.unshift(
          node.upsertLineSegment({
            id: existing.id,
            curve: conic && getSegmentCurve(conic),
          }),
        );
        recalculateLineSegmentMetaData(node, [existing.id]);
      } else {
        const style = existing && { width: existing.width, stripe: existing.stripe };

        if (isDefined(existing)) {
          changes.unshift(node.removeLineSegment(existing.id));
        }

        changes.unshift(
          ...addArcLineSegments(
            node,
            from,
            to,
            arcs,
            existing?.attributes ?? activeAttributes,
            style,
          ),
        );
      }

      node.refresh();
      tool.asset.refreshMetadata();
    },
    rollback: () => {
      node.revert(changes);
      node.refresh();
      tool.asset.refreshMetadata();
    },
  });

  return endPointId;
}
